import { useSyncExternalStore } from "react";

/**
 * Three modes, one hierarchy.
 *
 *   light     — default. Warm paper, near-black ink.
 *   dark      — the same system inverted, for night shift.
 *   inverted  — high-contrast black-on-white with structural rules, for low
 *               vision and washed-out command-wall displays. Not a new
 *               colourway: every tonal relationship pushed to its extreme so
 *               nothing depends on a subtle grey surviving the display.
 *
 * `inverted` deliberately reports colorScheme "light" — the browser should
 * render form controls and scrollbars against white, which is what the
 * operator's eyes expect when they have selected high-contrast.
 */
export type Theme = "light" | "dark" | "inverted";

export const THEME_ORDER: Theme[] = ["light", "dark", "inverted"];

const STORAGE_KEY = "yala:theme";

function isTheme(v: unknown): v is Theme {
  return v === "light" || v === "dark" || v === "inverted";
}

function readSavedTheme(): Theme | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return isTheme(v) ? v : null;
}

function systemTheme(): Theme {
  // Rams Operating System is light-first. Dark and inverted are opt-in.
  return "light";
}

// ── Shared single source of truth ────────────────────────────────────────
// Every useTheme() consumer (App, TopBar, …) must agree on the current theme.
// With separate useState copies, clicking the toggle re-themed the CSS (via the
// <html data-theme> attribute) but NOT App's own `theme` — which is the sole
// input to the MapLibre basemap — so the map lagged a render behind. A
// module-level store read through useSyncExternalStore keeps all subscribers
// in lockstep, so the basemap restyles the instant the toggle is clicked.
let currentTheme: Theme = readSavedTheme() ?? systemTheme();
const listeners = new Set<() => void>();

function applyToDom(theme: Theme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.setAttribute("data-theme", theme);
  root.style.colorScheme = theme === "inverted" ? "light" : theme;
}

// Reflect the initial theme on first import so the very first paint is correct.
applyToDom(currentTheme);

function setThemeGlobal(t: Theme): void {
  if (t === currentTheme) return;
  currentTheme = t;
  try {
    window.localStorage.setItem(STORAGE_KEY, t);
  } catch {
    // Safari private mode may throw
  }
  applyToDom(t);
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): Theme {
  return currentTheme;
}

function getServerSnapshot(): Theme {
  return "light";
}

export function useTheme(): { theme: Theme; setTheme: (t: Theme) => void; cycle: () => void; toggle: () => void } {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const cycle = () => {
    const i = THEME_ORDER.indexOf(currentTheme);
    setThemeGlobal(THEME_ORDER[(i + 1) % THEME_ORDER.length]);
  };

  return {
    theme,
    setTheme: setThemeGlobal,
    cycle,
    // `toggle` kept for the existing two-state callers; `cycle` is the
    // three-mode control the TopBar now uses.
    toggle: () => setThemeGlobal(currentTheme === "light" ? "dark" : "light"),
  };
}
